import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Folder, FolderOpen, ChevronRight, ChevronDown, Plus, Pencil, Trash2, Check, X, FolderInput, Inbox } from "lucide-react";

const UNCLASSIFIED = "__none__";

// Construit l'ensemble des descendants (inclus) d'un groupe à partir de la liste plate.
function descendantSet(groups, rootId) {
  const children = {};
  groups.forEach((g) => { (children[g.parent_id || ""] = children[g.parent_id || ""] || []).push(g.id); });
  const out = new Set(), stack = [rootId];
  while (stack.length) {
    const cur = stack.pop();
    if (out.has(cur)) continue;
    out.add(cur);
    (children[cur] || []).forEach((c) => stack.push(c));
  }
  return out;
}

export default function PoiGroupManager({ pois = [], companies = [], isAdmin = false, canManage = true, onEditPoi, onChanged }) {
  const multiCompany = companies.length > 1;
  const [companyId, setCompanyId] = useState("");
  const [groups, setGroups] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [filterGroup, setFilterGroup] = useState(null); // null=tous, UNCLASSIFIED, ou id
  const [selected, setSelected] = useState({}); // poi id -> bool
  const [moveTarget, setMoveTarget] = useState("");
  const [newName, setNewName] = useState("");
  const [newParent, setNewParent] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");

  useEffect(() => {
    if (multiCompany && !companyId && companies[0]) setCompanyId(companies[0].id);
  }, [multiCompany, companyId, companies]);

  const effCompany = multiCompany ? companyId : (companies[0]?.id || "");

  const loadGroups = useCallback(async () => {
    try {
      const r = await api.get("/poi-groups", { params: effCompany ? { company_id: effCompany } : {} });
      setGroups(r.data || []);
    } catch { setGroups([]); }
  }, [effCompany]);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  // POI du périmètre société courant
  const scopedPois = useMemo(
    () => (effCompany ? pois.filter((p) => p.company_id === effCompany) : pois),
    [pois, effCompany]
  );

  // POI visibles selon le filtre groupe (descendants inclus)
  const visiblePois = useMemo(() => {
    if (filterGroup === null) return scopedPois;
    if (filterGroup === UNCLASSIFIED) return scopedPois.filter((p) => !p.group_id);
    const set = descendantSet(groups, filterGroup);
    return scopedPois.filter((p) => p.group_id && set.has(p.group_id));
  }, [scopedPois, filterGroup, groups]);

  // Compteurs (direct + total descendants) par groupe
  const directCount = useMemo(() => {
    const m = {};
    scopedPois.forEach((p) => { if (p.group_id) m[p.group_id] = (m[p.group_id] || 0) + 1; });
    return m;
  }, [scopedPois]);

  const totalCount = useMemo(() => {
    const m = {};
    groups.forEach((g) => {
      const set = descendantSet(groups, g.id);
      let n = 0;
      scopedPois.forEach((p) => { if (p.group_id && set.has(p.group_id)) n += 1; });
      m[g.id] = n;
    });
    return m;
  }, [groups, scopedPois]);

  const unclassifiedCount = useMemo(() => scopedPois.filter((p) => !p.group_id).length, [scopedPois]);

  const childrenOf = useCallback((pid) => groups.filter((g) => (g.parent_id || null) === (pid || null)), [groups]);

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const createGroup = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Nom du groupe requis"); return; }
    try {
      await api.post("/poi-groups", { name, parent_id: newParent || null, company_id: effCompany || null });
      toast.success("Groupe créé");
      setNewName(""); setNewParent("");
      loadGroups();
    } catch (e) { toast.error(e?.response?.data?.detail || "Création impossible"); }
  };

  const saveRename = async (gid) => {
    const name = renameVal.trim();
    if (!name) { toast.error("Nom requis"); return; }
    const g = groups.find((x) => x.id === gid);
    try {
      await api.put(`/poi-groups/${gid}`, { name, parent_id: g?.parent_id || null });
      toast.success("Groupe renommé");
      setRenamingId(null); loadGroups();
    } catch (e) { toast.error(e?.response?.data?.detail || "Renommage impossible"); }
  };

  const deleteGroup = async (gid) => {
    try {
      const r = await api.delete(`/poi-groups/${gid}`);
      toast.success(`Groupe supprimé — ${r.data?.reclassed_pois || 0} POI reclassé(s) en Non classés`);
      if (filterGroup === gid) setFilterGroup(null);
      loadGroups();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
  };

  const moveSelected = async () => {
    if (!selectedIds.length) { toast.error("Sélectionnez au moins un POI"); return; }
    const target = moveTarget === UNCLASSIFIED ? null : (moveTarget || null);
    try {
      const r = await api.post("/pois/assign-group", { poi_ids: selectedIds, group_id: target });
      toast.success(`${r.data?.updated || 0} POI déplacé(s)`);
      setSelected({}); setMoveTarget("");
      loadGroups();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Déplacement impossible"); }
  };

  const groupName = (id) => groups.find((g) => g.id === id)?.name || "—";
  const inputCls = "bg-secondary/40 border border-border rounded-md px-2 py-1.5 text-sm outline-none focus:border-[#17A2B8]";

  const renderNode = (g, depth = 0) => {
    const kids = childrenOf(g.id);
    const isOpen = expanded[g.id];
    return (
      <div key={g.id} data-testid={`poi-group-node-${g.id}`}>
        <div className={`flex items-center gap-1 py-1 pr-2 rounded-md hover:bg-black/5 ${filterGroup === g.id ? "bg-[#17A2B8]/10" : ""}`}
          style={{ paddingLeft: `${8 + depth * 16}px` }}>
          {kids.length > 0 ? (
            <button type="button" onClick={() => toggleExpand(g.id)} data-testid={`poi-group-toggle-${g.id}`} className="text-muted-foreground hover:text-foreground">
              {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : <span className="w-4 h-4 inline-block" />}
          {isOpen ? <FolderOpen className="w-4 h-4 text-[#F5821F]" /> : <Folder className="w-4 h-4 text-[#F5821F]" />}
          {renamingId === g.id ? (
            <span className="flex items-center gap-1 flex-1">
              <input autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)} data-testid={`poi-group-rename-input-${g.id}`}
                className={inputCls + " flex-1"} onKeyDown={(e) => e.key === "Enter" && saveRename(g.id)} />
              <button type="button" onClick={() => saveRename(g.id)} data-testid={`poi-group-rename-save-${g.id}`} className="text-[#10B981]"><Check className="w-4 h-4" /></button>
              <button type="button" onClick={() => setRenamingId(null)} className="text-muted-foreground"><X className="w-4 h-4" /></button>
            </span>
          ) : (
            <>
              <button type="button" onClick={() => setFilterGroup(g.id)} data-testid={`poi-group-select-${g.id}`}
                className="flex-1 text-left text-sm font-medium truncate">{g.name}</button>
              <span className="text-[11px] text-muted-foreground tabular-nums" data-testid={`poi-group-count-${g.id}`}>
                {directCount[g.id] || 0}{totalCount[g.id] !== (directCount[g.id] || 0) ? ` / ${totalCount[g.id]}` : ""}
              </span>
              {canManage && (
                <>
                  <button type="button" onClick={() => { setRenamingId(g.id); setRenameVal(g.name); }} data-testid={`poi-group-rename-${g.id}`} className="text-muted-foreground hover:text-[#17A2B8]"><Pencil className="w-3.5 h-3.5" /></button>
                  <button type="button" onClick={() => deleteGroup(g.id)} data-testid={`poi-group-delete-${g.id}`} className="text-muted-foreground hover:text-[#EF4444]"><Trash2 className="w-3.5 h-3.5" /></button>
                </>
              )}
            </>
          )}
        </div>
        {isOpen && kids.map((k) => renderNode(k, depth + 1))}
      </div>
    );
  };

  const catLabel = (c) => ({ client: "Client", depot: "Dépôt", station: "Station", fournisseur: "Fournisseur", autre: "Autre" })[c] || c;
  const allVisibleSelected = visiblePois.length > 0 && visiblePois.every((p) => selected[p.id]);

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-4" data-testid="poi-group-manager">
      {/* Colonne arborescence */}
      <div className="rounded-lg border border-border bg-secondary/20 p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="label-mono">Groupes d'emplacements</p>
        </div>
        {multiCompany && (
          <select value={companyId} onChange={(e) => { setCompanyId(e.target.value); setFilterGroup(null); setSelected({}); }}
            data-testid="poi-group-company" className={inputCls + " w-full mb-3"}>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}

        <div className="space-y-0.5 mb-3">
          <button type="button" onClick={() => setFilterGroup(null)} data-testid="poi-group-filter-all"
            className={`w-full text-left text-sm font-semibold px-2 py-1.5 rounded-md hover:bg-black/5 ${filterGroup === null ? "bg-[#17A2B8]/10" : ""}`}>
            Tous les emplacements <span className="text-[11px] text-muted-foreground">({scopedPois.length})</span>
          </button>
          <div className={`flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-black/5 ${filterGroup === UNCLASSIFIED ? "bg-[#17A2B8]/10" : ""}`}>
            <Inbox className="w-4 h-4 text-muted-foreground" />
            <button type="button" onClick={() => setFilterGroup(UNCLASSIFIED)} data-testid="poi-group-unclassified"
              className="flex-1 text-left text-sm font-medium">Non classés <span className="text-[11px] text-muted-foreground">({unclassifiedCount})</span></button>
          </div>
          {childrenOf(null).map((g) => renderNode(g, 0))}
          {groups.length === 0 && <p className="text-xs text-muted-foreground px-2 py-2">Aucun groupe. Créez-en un ci-dessous.</p>}
        </div>

        {canManage && (
          <div className="border-t border-border pt-3 space-y-2">
            <p className="label-mono">Nouveau groupe</p>
            <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nom du groupe"
              data-testid="poi-group-new-name" className={inputCls + " w-full"} onKeyDown={(e) => e.key === "Enter" && createGroup()} />
            <select value={newParent} onChange={(e) => setNewParent(e.target.value)} data-testid="poi-group-new-parent" className={inputCls + " w-full"}>
              <option value="">— Racine (aucun parent) —</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
            <button type="button" onClick={createGroup} data-testid="poi-group-create-btn"
              className="w-full flex items-center justify-center gap-1.5 bg-[#17A2B8] hover:bg-[#1391a5] text-white font-semibold py-1.5 rounded-md text-sm">
              <Plus className="w-4 h-4" /> Créer le groupe
            </button>
          </div>
        )}
      </div>

      {/* Colonne liste POI + actions */}
      <div className="rounded-lg border border-border overflow-hidden">
        {canManage && (
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-border bg-secondary/20">
            <span className="text-xs text-muted-foreground" data-testid="poi-selected-count">{selectedIds.length} sélectionné(s)</span>
            <FolderInput className="w-4 h-4 text-muted-foreground ml-auto" />
            <select value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)} data-testid="poi-move-target" className={inputCls}>
              <option value="">— Déplacer vers… —</option>
              <option value={UNCLASSIFIED}>Non classés</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
            <button type="button" onClick={moveSelected} disabled={!selectedIds.length || !moveTarget} data-testid="poi-move-btn"
              className="text-xs font-semibold px-3 py-1.5 rounded-md bg-[#F5821F] text-white disabled:opacity-40">Déplacer</button>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-secondary/30 text-left text-xs text-muted-foreground">
            <tr>
              {canManage && <th className="px-3 py-2 w-8">
                <input type="checkbox" checked={allVisibleSelected} data-testid="poi-select-all"
                  onChange={(e) => { const n = { ...selected }; visiblePois.forEach((p) => { n[p.id] = e.target.checked; }); setSelected(n); }} />
              </th>}
              <th className="px-3 py-2">Nom</th>
              <th className="px-3 py-2">Catégorie</th>
              <th className="px-3 py-2">Groupe</th>
              <th className="px-3 py-2">Rayon</th>
            </tr>
          </thead>
          <tbody>
            {visiblePois.map((p) => (
              <tr key={p.id} className="border-t border-border hover:bg-[#17A2B8]/5" data-testid={`poi-mgr-row-${p.id}`}>
                {canManage && <td className="px-3 py-2">
                  <input type="checkbox" checked={!!selected[p.id]} data-testid={`poi-select-${p.id}`}
                    onChange={(e) => setSelected((s) => ({ ...s, [p.id]: e.target.checked }))} />
                </td>}
                <td className="px-3 py-2 font-semibold">
                  <button type="button" onClick={() => onEditPoi && onEditPoi({ ...p })} className="flex items-center gap-2 hover:text-[#17A2B8]">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />{p.name}
                  </button>
                </td>
                <td className="px-3 py-2">{catLabel(p.category)}</td>
                <td className="px-3 py-2">
                  {p.group_id
                    ? <span className="text-[11px] px-2 py-0.5 rounded bg-[#F5821F]/15 text-[#F5821F] font-semibold">{groupName(p.group_id)}</span>
                    : <span className="text-[11px] text-muted-foreground">Non classé</span>}
                </td>
                <td className="px-3 py-2">{p.radius ? `${p.radius} m` : "—"}</td>
              </tr>
            ))}
            {visiblePois.length === 0 && (
              <tr><td colSpan={canManage ? 5 : 4} className="px-3 py-6 text-center text-sm text-muted-foreground">Aucun POI dans cette vue.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
